import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import WorkflowProgress from "../../components/ui/WorkflowProgress";
import AccountConnectionIndicator from "../../components/ui/AccountConnectionIndicator";
import AccountStatusCard from "./components/AccountStatusCard";
import QuickStatsCard from "./components/QuickStatsCard";
import ActivityFeedItem from "./components/ActivityFeedItem";
import SyncedPlaylistCard from "./components/SyncedPlaylistCard";
import StartSyncCard from "./components/StartSyncCard";
import RealtimeNotification from "./components/RealtimeNotification";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useGlobalAuth } from "../../contexts/GlobalContext";

const DashboardSyncOverview = () => {
  const { spotify, youtube } = useGlobalAuth();

  const {
    isLoggedIn,
    spotifyUserInfo,
    spotifyLogin,
    spotifyLogout,
    getValidAccessToken,
    playlistCount,
    spotifyPlaylist,
  } = spotify;
  const {
    googleToken,
    googleUserInfo,
    youtubePlaylists,
    youtubeLogin,
    youtubeLogout,
  } = youtube;

  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [connections, setConnections] = useState({
    spotify: {
      connected: false,
      username: "",
      playlistCount: 0,
      lastSync: null,
      status: "disconnected",
    },
    youtube: {
      connected: false,
      username: "",
      playlistCount: 0,
      lastSync: null,
      status: "disconnected",
    },
  });
  useEffect(() => {
    if (spotifyUserInfo?.id) {
      setConnections((prev) => ({
        ...prev,
        spotify: {
          connected: true,
          username: spotifyUserInfo?.display_name,
          email: spotifyUserInfo?.email,
          playlistCount: playlistCount,
          lastSync: null,
          status: "active",
        },
      }));
    } else {
      setConnections((prev) => ({
        ...prev,
        spotify: {
          connected: false,
          username: null,
          email: null,
          playlistCount: 0,
          lastSync: null,
          status: "disconnected",
        },
      }));
    }
  }, [spotifyUserInfo, playlistCount]);

  useEffect(() => {
    if (googleUserInfo?.sub) {
      setConnections((prev) => ({
        ...prev,
        youtube: {
          connected: true,
          username: googleUserInfo.name,
          picture: googleUserInfo.picture,
          playlistCount: youtubePlaylists?.length,
          lastSync: null,
          status: "active",
        },
      }));
    } else {
      setConnections((prev) => ({
        ...prev,
        youtube: {
          connected: false,
          username: null,
          picture: null,
          playlistCount: 0,
          lastSync: null,
          status: "disconnected",
        },
      }));
    }
  }, [googleUserInfo, googleToken, youtubePlaylists]);

  const [stats] = useState({
    totalPlaylists: 15,
    songsTransferred: 1247,
    successRate: 94.2,
    activeSyncs: 2,
  });

  const [activities] = useState([
    {
      id: 1,
      playlistName: "My Favorites 2024",
      direction: "spotify_to_youtube",
      status: "completed",
      songsTransferred: 45,
      totalSongs: 45,
      timestamp: new Date(Date.now() - 1800000),
      duration: "2m 34s",
    },
    {
      id: 2,
      playlistName: "Workout Mix",
      direction: "youtube_to_spotify",
      status: "in_progress",
      songsTransferred: 12,
      totalSongs: 28,
      timestamp: new Date(Date.now() - 300000),
      duration: null,
    },
    {
      id: 3,
      playlistName: "Chill Vibes",
      direction: "spotify_to_youtube",
      status: "partial",
      songsTransferred: 18,
      totalSongs: 22,
      timestamp: new Date(Date.now() - 7200000),
      duration: "1m 45s",
    },
    {
      id: 4,
      playlistName: "Road Trip Songs",
      direction: "spotify_to_youtube",
      status: "failed",
      songsTransferred: 0,
      totalSongs: 67,
      timestamp: new Date(Date.now() - 86400000),
      duration: null,
    },
  ]);

  const [syncedPlaylists] = useState([
    {
      id: 1,
      name: "My Favorites 2024",
      coverImage:
        "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop",
      songCount: 45,
      sourcePlatform: "spotify",
      targetPlatform: "youtube",
      lastSync: new Date(Date.now() - 1800000),
      syncStatus: "up_to_date",
    },
    {
      id: 2,
      name: "Chill Vibes",
      coverImage:
        "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=100&h=100&fit=crop",
      songCount: 18,
      sourcePlatform: "spotify",
      targetPlatform: "youtube",
      lastSync: new Date(Date.now() - 7200000),
      syncStatus: "outdated",
    },
    {
      id: 3,
      name: "Electronic Beats",
      coverImage:
        "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=100&h=100&fit=crop",
      songCount: 32,
      sourcePlatform: "youtube",
      targetPlatform: "spotify",
      lastSync: new Date(Date.now() - 172800000),
      syncStatus: "sync_failed",
    },
  ]);

  const handleConnect = (platform) => {
    if (platform === "spotify") {
      spotifyLogin();
    } else if (platform === "youtube") {
      youtubeLogin();
    }
  };

  const handleDisconnect = (platform) => {
    if (platform === "spotify") {
      spotifyLogout();
    }
    if (platform === "youtube") {
      youtubeLogout();
    }
  };

  const handleViewDetails = (activityId) => {
    navigate("/sync-results-management", { state: { activityId } });
  };

  const handleManagePlaylist = (playlistId) => {
    navigate("/sync-results-management", { state: { playlistId } });
  };

  const handleResyncPlaylist = (playlistId) => {
    navigate("/playlist-selection-sync-setup", {
      state: { resyncPlaylistId: playlistId },
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const isConnected =
    connections.spotify.connected && connections.youtube.connected;

  return (
    <div className="min-h-screen bg-background">
      <WorkflowProgress />
      <main className="pt-14 lg:pt-16">
        <div className="px-4 lg:px-6 py-6 lg:py-8 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2">
                Dashboard
              </h1>
              <p className="text-text-secondary">
                Monitor your playlist syncs and manage connected accounts
              </p>
            </div>

            <div className="flex items-center space-x-3">
              {/* <AccountConnectionIndicator /> */}
              <Button
                variant="ghost"
                onClick={handleRefresh}
                iconName="RefreshCw"
                iconSize={18}
                // className={refreshing ? "animate-spin" : ""}
                disabled={refreshing}
              >
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>

          {/* Account Status Section */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Connected Accounts
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              {Object.entries(connections).map(([platform, connection]) => (
                <AccountStatusCard
                  key={platform}
                  platform={platform}
                  connection={connection}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                />
              ))}
            </div>
          </section>

          {/* Start Sync Section */}
          <section className="mb-8">
            <StartSyncCard isConnected={isConnected} />
          </section>

          {/* Quick Stats */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Quick Stats
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <QuickStatsCard
                icon="List"
                title="Total Playlists"
                value={stats.totalPlaylists}
                subtitle="Successfully synced"
                trend="+3"
                trendDirection="up"
              />
              <QuickStatsCard
                icon="Music"
                title="Songs Transferred"
                value={stats.songsTransferred.toLocaleString()}
                subtitle="Across all syncs"
                trend="+127"
                trendDirection="up"
              />
              <QuickStatsCard
                icon="Target"
                title="Success Rate"
                value={`${stats.successRate}%`}
                subtitle="Match accuracy"
                trend="+2.1%"
                trendDirection="up"
              />
              <QuickStatsCard
                icon="Activity"
                title="Active Syncs"
                value={stats.activeSyncs}
                subtitle="Currently running"
              />
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Recent Activity */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary">
                  Recent Activity
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/sync-results-management")}
                  iconName="ExternalLink"
                  iconSize={14}
                >
                  View All
                </Button>
              </div>

              <div className="space-y-3">
                {activities.slice(0, 5).map((activity) => (
                  <ActivityFeedItem
                    key={activity.id}
                    activity={activity}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            </section>

            {/* Synced Playlists */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary">
                  My Synced Playlists
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/sync-results-management")}
                  iconName="Settings"
                  iconSize={14}
                >
                  Manage
                </Button>
              </div>

              <div className="space-y-3">
                {syncedPlaylists.map((playlist) => (
                  <SyncedPlaylistCard
                    key={playlist.id}
                    playlist={playlist}
                    onManage={handleManagePlaylist}
                    onResync={handleResyncPlaylist}
                  />
                ))}
              </div>
            </section>
          </div>

          {/* Empty State for New Users */}
          {!isConnected && (
            <section className="mt-8 text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="flex items-center justify-center w-16 h-16 bg-surface-secondary rounded-full mx-auto mb-4">
                  <Icon name="Music" size={32} className="text-text-muted" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  Welcome to PlaylistSync!
                </h3>
                <p className="text-text-secondary mb-6">
                  Connect your music streaming accounts to start syncing your
                  playlists seamlessly between platforms.
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate("/authentication-account-connection")}
                  iconName="Link"
                  iconSize={18}
                >
                  Get Started
                </Button>
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardSyncOverview;
